import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, Sparkles, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PopupConfigurator from '../components/PopupConfigurator';
import { useLanguage } from '../context/LanguageContext';

const PopupPhotoPage = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = t('popup.benefits.items');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      data-testid="popup-photo-page"
    >
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.pexels.com/photos/27368850/pexels-photo-27368850.jpeg?auto=compress&cs=tinysrgb&w=2000"
              alt="Resort pool with vacationers and activities"
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]" />
          </div>

          {/* Orange Glow */}
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-[#FF7A00]/10 rounded-full blur-[150px] pointer-events-none" />

          <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF7A00]/10 border border-[#FF7A00]/20 mb-6"
              >
                <Camera className="w-4 h-4 text-[#FF7A00]" />
                <span className="text-sm text-[#FF7A00] font-medium">{t('popup.title')}</span>
              </motion.div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
              >
                <span className="text-white">{t('popup.hero.title').split(' ').slice(0, 2).join(' ')} </span>
                <span className="text-gradient">{t('popup.hero.title').split(' ').slice(2).join(' ')}</span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg md:text-xl text-white/60 mb-10"
              >
                {t('popup.hero.description')}
              </motion.p>

              {/* Quick Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap items-center justify-center gap-6"
              >
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-white/70" data-testid={`popup-benefit-${index}`}>
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Configurator Section */}
        <section className="py-16 md:py-24 relative" data-testid="configurator-section">
          {/* Background Glow */}
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#FF7A00]/5 rounded-full blur-[200px] pointer-events-none" />

          <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
            {/* Section Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm tracking-widest uppercase mb-4">
                Configurateur
              </span>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                {t('popup.configurator.title')}
              </h2>
              <p className="text-lg text-white/60 max-w-xl mx-auto">
                {t('popup.subtitle')}
              </p>
            </motion.div>

            {/* Configurator Component */}
            <PopupConfigurator />
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 md:py-24 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                {t('popup.benefits.title')}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Sparkles, title: 'Expérience Unique', desc: 'Offrez à vos clients des souvenirs mémorables' },
                { icon: CheckCircle, title: 'Revenus Additionnels', desc: '20% de commission sur chaque vente' },
                { icon: Camera, title: 'Qualité Pro', desc: 'Photos HD par des professionnels' },
                { icon: ArrowRight, title: 'Installation Rapide', desc: 'Mise en place en quelques jours' }
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    data-testid={`popup-feature-${index}`}
                    className="glass-card rounded-2xl p-6 text-center hover:border-[#FF7A00]/30 transition-colors"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center">
                      <Icon className="w-7 h-7 text-[#FF7A00]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm text-white/50">{feature.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* Back to Home */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-12 text-center"
            >
              <Link
                to="/"
                data-testid="back-to-home"
                className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF7A00] transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Retour à l'accueil
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </motion.div>
  );
};

export default PopupPhotoPage;
